import { StaticImageData } from 'next/image'
import React from 'react'

export type ICard = {
  title: string
  description: string
  /** Legacy — retained so call sites don't break. Ignored in render. */
  image?: StaticImageData | string
  /** Legacy — retained so call sites don't break. Ignored in render. */
  number?: StaticImageData | string
  alt?: string
  hideTitle?: boolean
  index?: number
}

interface IProps {
  card: ICard
  /** Legacy — kept for back-compat with existing call sites. */
  cardBgColor?: string
  /** Legacy — kept for back-compat with existing call sites. */
  noGradient?: boolean
  index?: number
  /**
   * Below md, lay the step out as a row (numeral beside the copy) instead of
   * the tall editorial column. Used when the parent stacks all steps on one
   * phone screen — three 112px numerals stacked ran to 1.6 viewports.
   */
  compact?: boolean
}

/**
 * Editorial "how it works" step.
 *
 * Replaces the legacy flat-illustration card with a typography-led layout:
 * oversized Fraunces numeral, thin rule, display-weight title, short
 * supporting copy. Works inside the existing Carousel at any background.
 */
const StepCard = ({
  card: { title, description, hideTitle },
  index,
  compact
}: IProps) => {
  const stepNumber = String((index ?? 0) + 1).padStart(2, '0')

  return (
    <article
      className={`flex w-full max-w-[360px] min-w-[260px] text-left sm:min-w-[300px] ${
        compact
          ? 'flex-row items-start gap-4 px-2 py-2 md:flex-col md:gap-5 md:pt-2 md:pb-8'
          : 'flex-col gap-5 px-2 pt-2 pb-8'
      }`}
    >
      <span
        aria-hidden="true"
        className={`font-display font-medium tracking-[-0.04em] text-blue-500 ${
          compact
            ? 'w-12 shrink-0 text-[40px] leading-none md:w-auto md:text-[112px] md:leading-[0.85]'
            : 'text-[96px] leading-[0.85] md:text-[112px]'
        }`}
      >
        {stepNumber}
      </span>
      <div className={`flex flex-col ${compact ? 'gap-2 md:gap-5' : 'gap-5'}`}>
        <span
          className={`h-px w-14 bg-ink-100/30 ${compact ? 'hidden md:block' : 'block'}`}
          aria-hidden="true"
        />
        {!hideTitle ? (
          <h3
            className={`font-display leading-[1.15] font-medium text-ink-700 ${
              compact ? 'text-xl md:text-[26px]' : 'text-2xl md:text-[26px]'
            }`}
          >
            {title}
          </h3>
        ) : null}
        <p className="max-w-[300px] text-[15px] leading-relaxed text-ink-200">
          {description}
        </p>
      </div>
    </article>
  )
}

export default React.memo(StepCard)
